<template>
  <div v-if="content" class="m-auto w-full max-w-[56rem]">
    <h1>{{ content.title }}</h1>
    <div v-if="content.feature_image" aria-hidden="true" class="relative border-b border-gray-200 mb-4">
      <img
        :src="content.feature_image"
        :alt="content.title"
        :data-pin-description="content.meta_description || content.title"
        class="relative w-full object-cover object-center max-h-96"
      />
    </div>
    <TrustedHtml :html="content.html" />
  </div>
</template>

<script setup lang="ts">
import { captureError } from '~/lib/logger'
import type { GhostTag } from '~/types/ghost-tag'

const props = defineProps<{
  slug: string
  tag: GhostTag
}>()
const { getGhostPageContent } = useGhost()
const { error, data: content } = await useAsyncData('ghost-content', async () => {
  return await getGhostPageContent({ slug: props.slug, tag: props.tag })
})
if (error.value) captureError(toRaw(error.value) ?? new Error('Error getting ghost content!'))
</script>

<style scoped lang="scss">
:deep() {
  ul {
    font-size: 1.875rem;
    line-height: 2.25rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
  }

  h1 {
    font-size: 2rem;
    line-height: 2.25rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }

  h2 {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 600;
    margin-bottom: 0.3rem;
  }

  h3 {
    font-size: 1.35rem;
    line-height: 2.15rem;
    font-weight: 700;
    margin-bottom: 0.3rem;
  }

  p {
    font-size: 1.125rem;
    font-style: normal;
    line-height: 1.75rem;
    padding-bottom: 0.5rem;
  }

  hr {
    margin: 1.5rem 0;
  }

  ol,
  ul {
    font-size: 1.125rem;
    line-height: 1.9375rem;
    padding: 0;
    margin-bottom: 1rem;
    list-style-position: inside;

    li {
      margin-left: 1rem;
    }
  }

  ul {
    list-style-type: disc;
  }

  ol {
    list-style-type: decimal;
  }

  figure {
    display: flex;
    flex-direction: column;
    grid-column: 1 / -1;
    justify-content: center;
    overflow: hidden;
    margin-bottom: 2.5rem;
    margin-top: 1.25rem;

    img {
      align-self: center;
      border-radius: 0.5rem;
      height: auto;
      max-height: 100%;
      max-width: 100%;
    }

    figcaption {
      color: $primary-300;
      height: 2rem;
      line-height: 2rem;
      text-align: center;
    }

    blockquote.instagram-media {
      margin: auto !important;
      max-height: 100%;
    }

    &.kg-width-full {
      max-height: 75vh;
      margin-left: 0;
      margin-right: 0;
      grid-column: 1 / -1;
      margin-bottom: 2.5rem;
      margin-top: 1.25rem;

      img {
        width: 100%;
        height: auto;
        border-radius: 0;
      }
    }

    &.kg-width-wide {
      img {
        width: 100%;
        height: auto;
      }
    }

    &.kg-embed-card {
      display: initial;
      max-height: 100%;

      iframe {
        margin-left: auto !important ;
        margin-right: auto !important ;
      }

      iframe[src*='player.vimeo.com'],
      iframe[src*='youtube.com/embed'] {
        width: 100%;
        height: 100%;
        overflow: hidden;
        margin-bottom: 36%;
      }

      &.kg-card-hascaption {
        figcaption {
          display: none;
        }
      }
    }

    &.kg-card-hascaption {
      img {
        max-height: calc(100% - 2rem);
      }
    }
  }

  a {
    color: $highlight-500;
  }

  a:hover {
    text-decoration: underline;
    color: $primary-500;
  }

  blockquote {
    margin: 0 0 1rem;
    &:not(.alternative) {
      background-color: $highlight-75;
      border-radius: 0.5rem;
      color: $primary-400;
      padding: 2rem;

      & > * {
        &:not(a) {
          color: $primary-500;
          margin: 0;
          padding: 0;
        }
      }
    }

    &.alternative {
      font-weight: 600;

      border-left: solid $highlight-500 0.3125rem;
      padding-left: 1.1875rem;
      font-size: 1.3125rem;
      line-height: 1.9375rem;
      color: $primary-400;

      > * {
        font-weight: 600;
        font-size: 1.3125rem;
        line-height: 1.9375rem;
        color: $primary-400;
        padding: 0;
        margin: 0;
      }
    }
  }

  .kg-align-center {
    text-align: center;
  }

  .kg-btn {
    background: $primary-500;
    color: #fff;
    padding: 0.75rem;
    line-height: 4rem;
    border-radius: 0.5rem;
    font-weight: bold;
  }

  .kg-btn:hover {
    color: #fff;
    background-color: $highlight-500;
    text-decoration: none;
  }
}
</style>
